// apps/admin/src/hooks/use-interval.ts
//
// P5 UI Polish W6 T7 — Visibility-aware repeating interval hook.
//
// Calls `callback` every `delay` ms while the document is visible.
// Pauses when `document.hidden === true` (Page Visibility API) and resumes
// on the next tick after the tab becomes active again. Set `delay` to null
// to disable (no interval is registered).
//
// Pattern: savedCallback ref avoids re-binding the setInterval when the
// caller's closure identity changes between renders. Identity-stable.

import { useEffect, useRef } from 'react';

/**
 * Calls `callback` every `delay` ms while the document is visible.
 * Paused via the Page Visibility API when `document.hidden === true`.
 * Set `delay` to null to disable the interval.
 */
export function useInterval(callback: () => void, delay: number | null): void {
  const savedCallback = useRef<() => void>(callback);

  // Always keep the ref in sync with the latest callback, without re-binding
  // the interval.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;

    let active = true;

    const tick = (): void => {
      if (!active) return;
      // Skip the tick when the page is in a background tab.
      if (typeof document !== 'undefined' && document.hidden) return;
      savedCallback.current();
    };

    const id = setInterval(tick, delay);

    return (): void => {
      active = false;
      clearInterval(id);
    };
  }, [delay]);
}
